/** @jsxImportSource ../../i18n */
import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { logout } from '../../store/slices/authSlice';
import { useLanguage } from '../../contexts/LanguageContext';
import { cx } from '../../theme/cx';
import { FloatingTooltip, Logo, SegmentedControl, UserAvatar } from '../ui';
import { NAV_SECTIONS } from './navConfig';

// Section colours: the trading section in its blue, the others in the
// theme's gold (titles and icons).
const TONES = {
  trade: { label: 'text-trade-400', icon: 'text-trade-400/80 group-hover:text-trade-300', rule: 'bg-trade-500/60' },
  gold: { label: 'text-gold-400', icon: 'text-gold-400/80 group-hover:text-gold-300', rule: 'bg-gold-500/50' },
};

const NavItem = ({ item, collapsed, onNavigate, tone }) => {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => cx(
        'group relative flex h-10 items-center gap-3 rounded-control text-sm font-medium transition-colors duration-150',
        collapsed ? 'w-10 justify-center' : 'px-3',
        isActive
          ? 'bg-surface-2 text-fg before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-gold-500'
          : 'text-fg-muted hover:bg-surface-2/60 hover:text-fg',
      )}
    >
      {({ isActive }) => (
        <>
          <Icon size={18} aria-hidden="true" className={cx('flex-shrink-0 transition-colors', isActive ? 'text-gold-300' : TONES[tone].icon)} />
          <span className={collapsed ? 'sr-only' : 'truncate'}>{item.label}</span>
        </>
      )}
    </NavLink>
  );
  return collapsed ? <FloatingTooltip content={item.label}>{link}</FloatingTooltip> : link;
};

const Hint = ({ collapsed, content, children }) => (collapsed ? <FloatingTooltip content={content}>{children}</FloatingTooltip> : children);

const FOOTER_BUTTON = 'flex h-10 w-full items-center gap-3 rounded-control text-sm font-medium text-fg-muted transition-colors duration-150';

export const Sidebar = ({ collapsed = false, onToggleCollapse, onNavigate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { language, setLanguage } = useLanguage();

  const handleLogout = () => {
    onNavigate?.();
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="flex h-full flex-col">
      <div className={cx('flex h-[var(--topbar-h)] flex-shrink-0 items-center border-b border-line', collapsed ? 'justify-center' : 'px-5')}>
        <Link to="/dashboard" onClick={onNavigate} aria-label="PEA Wallet, tableau de bord" className="rounded-control">
          <Logo size={34} withWordmark={!collapsed} />
        </Link>
      </div>

      <nav aria-label="Navigation principale" className={cx('flex-1 overflow-y-auto py-5', collapsed ? 'px-[18px]' : 'px-3')}>
        {NAV_SECTIONS.map((section) => {
          const tone = section.tone === 'trade' ? 'trade' : 'gold';
          return (
            <div key={section.id} className={cx('mb-6 last:mb-0', tone === 'gold' && 'universe-default')}>
              {collapsed
                ? <div aria-hidden="true" className={cx('mx-auto mb-3 h-px w-6', TONES[tone].rule)} />
                : (
                  <p className={cx('eyebrow mb-2 flex items-center gap-2 px-3', TONES[tone].label)}>
                    {section.label}
                    {section.tone === 'trade' && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-trade-400 shadow-[0_0_8px_rgb(var(--c-trade-400))]" />}
                  </p>
                )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}><NavItem item={item} collapsed={collapsed} onNavigate={onNavigate} tone={tone} /></li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      <div className={cx('flex-shrink-0 space-y-1 border-t border-line py-3', collapsed ? 'px-[18px]' : 'px-3')}>
        {!collapsed && (
          <div className="px-2 pb-3 pt-1">
            <p className="eyebrow mb-2">Langue (interface et IA)</p>
            <SegmentedControl
              ariaLabel="Langue de l’interface et des contenus IA"
              value={language}
              onChange={setLanguage}
              fullWidth
              options={[{ value: 'French', label: 'Français' }, { value: 'English', label: 'English' }]}
            />
          </div>
        )}
        <div className={cx('flex items-center gap-3 py-2', collapsed ? 'justify-center' : 'px-2')}>
          <UserAvatar email={user?.email} size="sm" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-fg">{user?.email}</p>
              <p className="text-xs text-fg-subtle">Investisseur</p>
            </div>
          )}
        </div>
        <Hint collapsed={collapsed} content="Se déconnecter">
          <button type="button" onClick={handleLogout} className={cx(FOOTER_BUTTON, 'hover:bg-loss-900/50 hover:text-loss-300', collapsed ? 'justify-center' : 'px-3')} aria-label={collapsed ? 'Se déconnecter' : undefined}>
            <LogOut size={18} aria-hidden="true" />
            {!collapsed && 'Se déconnecter'}
          </button>
        </Hint>
        {onToggleCollapse && (
          <Hint collapsed={collapsed} content="Déplier le menu">
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
              className={cx(FOOTER_BUTTON, 'hover:bg-surface-2 hover:text-fg', collapsed ? 'justify-center' : 'px-3')}
            >
              {collapsed ? <PanelLeftOpen size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
              {!collapsed && <span>Replier le menu</span>}
            </button>
          </Hint>
        )}
      </div>
    </div>
  );
};
